import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { chatService } from "../service/chatService";
import { BlockReason } from "../types";
import styles from "./BlockUserModal.module.scss";

interface BlockUserModalProps {
  visible: boolean;
  otherUserName: string;
  blocking: boolean;
  onConfirm: (reasonId?: number, reasonText?: string) => void;
  onHide: () => void;
}

export const BlockUserModal: React.FC<BlockUserModalProps> = ({
  visible,
  otherUserName,
  blocking,
  onConfirm,
  onHide,
}) => {
  const [reasons, setReasons] = useState<BlockReason[]>([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [selectedTid, setSelectedTid] = useState<number | "other" | null>(null);
  const [otherText, setOtherText] = useState("");

  useEffect(() => {
    if (!visible) return;
    setSelectedTid(null);
    setOtherText("");
    setLoadingReasons(true);
    chatService
      .getBlockReasons()
      .then(setReasons)
      .finally(() => setLoadingReasons(false));
  }, [visible]);

  const canConfirm =
    selectedTid !== null &&
    (selectedTid !== "other" || otherText.trim().length > 0);

  const handleConfirm = () => {
    if (selectedTid === "other") {
      onConfirm(undefined, otherText.trim());
    } else if (typeof selectedTid === "number") {
      onConfirm(selectedTid);
    }
  };

  const footer = (
    <div className={styles.footer}>
      <Button label="Cancel" className="p-button-text" onClick={onHide} disabled={blocking} />
      <Button
        label="Block"
        icon={blocking ? "pi pi-spin pi-spinner" : "pi pi-ban"}
        className="p-button-danger"
        onClick={handleConfirm}
        disabled={!canConfirm || blocking}
      />
    </div>
  );

  return (
    <Dialog
      header={`Block ${otherUserName}`}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "min(480px, 95vw)" }}
      modal
      closable={!blocking}
    >
      <p className={styles.description}>
        Are you sure you want to block <strong>{otherUserName}</strong>? This action
        will prevent them from sending messages to you.
      </p>

      {loadingReasons ? (
        <div className={styles.loadingReasons}>
          <ProgressSpinner style={{ width: "1.5rem", height: "1.5rem" }} />
        </div>
      ) : (
        <div className={styles.reasons}>
          <p className={styles.reasonsLabel}>Select a reason:</p>
          {reasons.map((reason) => (
            <div key={reason.tid} className={styles.reasonRow}>
              <RadioButton
                inputId={`reason-${reason.tid}`}
                value={reason.tid}
                checked={selectedTid === reason.tid}
                onChange={() => setSelectedTid(reason.tid)}
              />
              <label htmlFor={`reason-${reason.tid}`} className={styles.reasonLabel}>
                {reason.name}
              </label>
            </div>
          ))}
          <div className={styles.reasonRow}>
            <RadioButton
              inputId="reason-other"
              value="other"
              checked={selectedTid === "other"}
              onChange={() => setSelectedTid("other")}
            />
            <label htmlFor="reason-other" className={styles.reasonLabel}>
              Other reason
            </label>
          </div>
          {selectedTid === "other" && (
            <InputTextarea
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              placeholder="Please describe the reason…"
              rows={3}
              autoResize
              className={styles.otherTextarea}
            />
          )}
        </div>
      )}
    </Dialog>
  );
};
